import React, { useState } from 'react';
import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import {
  Sheet,
  SheetContent,
  SheetTrigger,
} from '@/components/ui/sheet';
import {
  Menu,
  X,
  Home,
  Calendar,
  MessageCircle,
  User,
  Heart,
  Settings,
  LogOut,
  Search,
  Bell,
} from 'lucide-react';
import { AvatarWithBadge } from '@/components';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';

const MainLayout = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  
  const isActive = (path) => location.pathname === path;
  
  const mainNavItems = [
    { path: '/', label: 'Home', icon: Home },
    { path: '/events', label: 'Events', icon: Calendar },
    // Restore in Phase 8
    // { path: '/matches', label: 'Matches', icon: Heart },
    // { path: '/messages', label: 'Messages', icon: MessageCircle },
    { path: '/my-events', label: 'My Events', icon: Calendar },
    { path: '/profile', label: 'Profile', icon: User },
  ];
  
  const handleLogout = async () => {
    await logout();
    navigate('/');
  };
  
  return (
    <div className="min-h-screen bg-neutral-50">
      {/* Desktop Header */}
      <header className="sticky top-0 z-40 bg-white border-b border-neutral-200 shadow-soft">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center space-x-2">
              <img src="/logo-192.png" alt="Dostination" className="w-10 h-10 object-contain" />
              <span className="font-display text-2xl font-bold text-primary-800 hidden md:block">
                Dostination
              </span>
            </Link>
            
            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center space-x-1">
              {mainNavItems.map((item) => {
                const Icon = item.icon;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`
                      flex items-center space-x-2 px-4 py-2 rounded-lg transition-all
                      ${isActive(item.path)
                        ? 'bg-primary-100 text-primary-800 font-semibold'
                        : 'text-neutral-700 hover:bg-neutral-100'
                      }
                    `}
                  >
                    <Icon className="h-5 w-5" />
                    <span>{item.label}</span>
                  </Link>
                );
              })}
            </nav>
            
            {/* Right Actions */}
            <div className="flex items-center space-x-3">
              {/* Search */}
              <Button variant="ghost" size="icon" className="hidden md:flex">
                <Search className="h-5 w-5" />
              </Button>
              
              {/* Notifications */}
              <Button variant="ghost" size="icon" className="relative">
                <Bell className="h-5 w-5" />
                <span className="absolute top-1 right-1 w-2 h-2 bg-accent-500 rounded-full"></span>
              </Button>
              
              {/* User Menu - visible on all viewports so mobile users
                  have a first-class logout path from the header. The
                  Sheet-based nav still handles primary route switching. */}
              <div className="flex items-center space-x-2">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      type="button"
                      className="focus:outline-none focus:ring-2 focus:ring-secondary-400 rounded-full cursor-pointer"
                      aria-label="Open account menu"
                      data-testid="main-nav-user-menu-trigger"
                    >
                      <AvatarWithBadge
                        size="md"
                        fallback={`${user?.first_name?.[0] || ''}${user?.last_name?.[0] || ''}`}
                        verified={user?.is_verified}
                        verificationType={user?.is_premium ? 'premium' : 'id'}
                      />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    <DropdownMenuLabel className="truncate">
                      <div className="text-sm font-semibold text-primary-900 truncate">
                        {user?.first_name} {user?.last_name}
                      </div>
                      <div className="text-xs text-neutral-500 truncate font-normal">{user?.email}</div>
                    </DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => navigate('/profile')} data-testid="main-nav-menu-profile">
                      <User className="h-4 w-4 mr-2" /> Profile
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/settings/security')} data-testid="main-nav-menu-security">
                      <Settings className="h-4 w-4 mr-2" /> Security
                    </DropdownMenuItem>
                    {['admin', 'super_admin'].includes(user?.role) && (
                      <DropdownMenuItem onClick={() => navigate('/admin')} data-testid="main-nav-menu-admin">
                        <Settings className="h-4 w-4 mr-2" /> Admin
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={handleLogout}
                      data-testid="main-nav-menu-logout"
                      className="text-accent-700 focus:text-accent-800"
                    >
                      <LogOut className="h-4 w-4 mr-2" /> Log out
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
              
              {/* Mobile Menu Toggle */}
              <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
                <SheetTrigger asChild className="md:hidden">
                  <Button variant="ghost" size="icon">
                    <Menu className="h-6 w-6" />
                  </Button>
                </SheetTrigger>
                <SheetContent side="right" className="w-[300px] sm:w-[400px]">
                  <div className="flex flex-col h-full">
                    {/* Mobile Menu Header */}
                    <div className="flex items-center justify-between mb-8">
                      <span className="font-display text-xl font-bold text-primary-800">
                        Menu
                      </span>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        <X className="h-6 w-6" />
                      </Button>
                    </div>
                    
                    {/* User Profile in Mobile Menu */}
                    <Link
                      to="/profile"
                      className="flex items-center space-x-3 p-4 rounded-lg bg-neutral-100 mb-6"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <AvatarWithBadge
                        size="lg"
                        fallback={`${user?.first_name?.[0] || ''}${user?.last_name?.[0] || ''}`}
                        verified={user?.is_verified}
                        verificationType={user?.is_premium ? 'premium' : 'id'}
                      />
                      <div>
                        <p className="font-semibold text-primary-900">
                          {user?.first_name} {user?.last_name}
                        </p>
                        <p className="text-sm text-neutral-600">View Profile</p>
                      </div>
                    </Link>
                    
                    {/* Mobile Navigation */}
                    <nav className="flex-1 space-y-1">
                      {mainNavItems.map((item) => {
                        const Icon = item.icon;
                        return (
                          <Link
                            key={item.path}
                            to={item.path}
                            onClick={() => setMobileMenuOpen(false)}
                            className={`
                              flex items-center space-x-3 px-4 py-3 rounded-lg transition-all
                              ${isActive(item.path)
                                ? 'bg-primary-100 text-primary-800 font-semibold'
                                : 'text-neutral-700 hover:bg-neutral-100'
                              }
                            `}
                          >
                            <Icon className="h-5 w-5" />
                            <span>{item.label}</span>
                          </Link>
                        );
                      })}
                      
                      <hr className="my-4" />
                      
                      <Link
                        to="/settings"
                        onClick={() => setMobileMenuOpen(false)}
                        className="flex items-center space-x-3 px-4 py-3 rounded-lg text-neutral-700 hover:bg-neutral-100 transition-all"
                      >
                        <Settings className="h-5 w-5" />
                        <span>Settings</span>
                      </Link>
                    </nav>
                    
                    {/* Logout Button */}
                    <Button
                      variant="outline"
                      className="w-full justify-start"
                      onClick={handleLogout}
                    >
                      <LogOut className="h-5 w-5 mr-3" />
                      Logout
                    </Button>
                  </div>
                </SheetContent>
              </Sheet>
            </div>
          </div>
        </div>
      </header>
      
      {/* Main Content */}
      <main className="container mx-auto px-4 py-8">
        <Outlet />
      </main>
      
      {/* Mobile Bottom Navigation */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-neutral-200 shadow-soft z-40">
        <div className="flex items-center justify-around h-16">
          {mainNavItems.slice(0, 5).map((item) => {
            const Icon = item.icon;
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`
                  flex flex-col items-center justify-center space-y-1 px-3 py-2 rounded-lg transition-all
                  ${isActive(item.path)
                    ? 'text-primary-700'
                    : 'text-neutral-500'
                  }
                `}
              >
                <Icon className={`h-6 w-6 ${isActive(item.path) ? 'scale-110' : ''}`} />
                <span className="text-xs font-medium">{item.label}</span>
              </Link>
            );
          })}
        </div>
      </nav>
      
      {/* Add padding bottom on mobile to account for fixed nav */}
      <div className="h-16 md:hidden"></div>
    </div>
  );
};

export default MainLayout;
